import type { OverlayTriggerState } from "@react-stately/overlays";
import { type RefObject, useEffect, useRef } from "react";
import { mergeProps, useMenuTrigger } from "react-aria";
import { useMenuTriggerState } from "react-stately";

import type { OverlayOptions } from "../types/overlay.d.ts";
import { useAnimeState } from "./useAnimeState.ts";
import { useOverlayWithPosition } from "./useOverlayWithPosition.ts";

export interface ContextualMenuOptions {
  isOpen?: boolean;
  isDisabled?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export const useContextualMenu = <T extends HTMLElement, U extends object>(
  triggerRef: RefObject<T | null>,
  options?: ContextualMenuOptions,
) => {
  const state = useMenuTriggerState({ isOpen: options?.isOpen, onOpenChange: options?.onOpenChange });
  const { menuTriggerProps: triggerProps, menuProps } = useMenuTrigger<U>(
    { isDisabled: options?.isDisabled, type: "menu" },
    state,
    triggerRef,
  );

  return {
    state,
    triggerProps,
    menuProps,
  };
};

/**
 * Animation and Offset are not configurable to keep thing simple
 * @deprecated
 */
export const useMenu = <T extends HTMLElement, M extends HTMLElement>(
  options?: OverlayOptions,
  triggerRef?: RefObject<T | null> | null,
  menuRef?: RefObject<M | null> | null,
) => {
  let innerTriggerRef = useRef<T>(null);
  let innerMenuRef = useRef<M>(null);
  innerTriggerRef = triggerRef ?? innerTriggerRef;
  innerMenuRef = menuRef ?? innerMenuRef;

  const state = useMenuTriggerState({ isOpen: options?.isOpen });
  const { menuTriggerProps: triggerProps, menuProps } = useMenuTrigger(
    { isDisabled: options?.isDisabled, type: "menu" },
    state,
    innerTriggerRef,
  );

  const {
    overlayProps,
    placement,
    arrowProps,
    // triggerProps: overlayTriggerProps,
  } = useOverlayWithPosition(innerTriggerRef, innerMenuRef, state, {
    placement: "bottom",
    offset: 5,
    ...options,
  });
  // TODO: This also should be move to the MenuPopover?
  const animeState = useAnimeState(innerMenuRef, state, placement!);

  // Close the menu on window resize
  useEffect(() => {
    const close = () => {
      state.close();
    };
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("resize", close);
    };
  }, [state]);

  return {
    triggerRef: innerTriggerRef,
    menuRef: innerMenuRef,
    // triggerProps: mergeProps(triggerProps, overlayTriggerProps),
    triggerProps,
    arrowProps,
    menuProps: mergeProps(menuProps, overlayProps),
    state: animeState as OverlayTriggerState,
  };
};
